import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import DonutChart from './DonutChart';
import { fetchPolygonSnapshotPrice, fetchUsStockSymbols } from '../composables/fetchPolygon';



type Batch = {
  qty: number;
  price: number;
};

type BuySell = {
  ticker: string;
  action: string;
  reason: string;
  amount?: number; // สำหรับ AD
  narrative: string;
  proportion?: number;
  batches: Batch[]; // เพิ่ม batches ในโครงสร้าง
};

type RealtimePortfolio = {
  guruId: string;
  portfolioName: string;
  guruType: string;
  createdAt: string;
  lastUpdated: string;
  telegramAnnounceAt: string | null;
  updatedAt: string;
  realtimeId: string;
  amount?: number;
  buys: BuySell[]; // ใช้โครงสร้าง BuySell
  sells: BuySell[]; // ใช้โครงสร้าง BuySell
};
type Portfolio = {
  id: string;
  name: string;
  description?: string;
};

type GuruOption = {
  name: string;
  id: string;
  portfolios: Portfolio[];
};

const TickerDropdown: React.FC<{
  value: string;
  onChange: (ticker: string, price?: number) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}> = ({ value, onChange, placeholder, className, disabled }) => {
  const [query, setQuery] = useState(value);
  const [dropdown, setDropdown] = useState(false);
  const [results, setResults] = useState<{ symbol: string; name: string; price?: number }[]>([]);
  const [loading, setLoading] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => { setQuery(value); }, [value]);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.trim().toUpperCase();
    setQuery(val);

    // อัปเดตค่าทันทีที่พิมพ์ (ทำให้ไม่ต้องคลิกเลือกก็ได้)
    onChange(val);

    if (typingTimeout) clearTimeout(typingTimeout);
    if (val.length < 1) {
      setResults([]);
      setDropdown(false);
      return;
    }

    setLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const res = await fetchUsStockSymbols(val);
        const withPrices = await Promise.all(
          res.map(async (item) => {
            const price = await fetchPolygonSnapshotPrice(item.symbol);
            return { ...item, price };
          })
        );
        setResults(withPrices);
        setDropdown(true);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }, 300);
    setTypingTimeout(timeout);
  };

  return (
    <div className="relative w-full">
      <input
        className={className || "w-full bg-[#2a2a32] text-white px-2 py-1.5 rounded border border-transparent focus:border-yellow-500 outline-none font-bold"}
        type="text"
        placeholder={placeholder || "TICKER"}
        value={query}
        onChange={handleInput}
        disabled={disabled}
        onFocus={() => query.length > 0 && setDropdown(true)}
        onBlur={() => setTimeout(() => setDropdown(false), 200)}
      />
      {dropdown && results.length > 0 && (
        <ul className="absolute left-0 right-0 bg-[#18181b] border border-gray-700 rounded-lg shadow-2xl mt-1 z-[999] max-h-60 overflow-y-auto">
          {results.map((item) => (
            <li
              key={item.symbol}
              className="flex items-center justify-between px-4 py-2 hover:bg-yellow-500/10 cursor-pointer border-b border-white/5 transition"
              onMouseDown={() => {
                onChange(item.symbol, item.price); // ส่งราคาแถมไปด้วยถ้ามีการคลิกเลือก
                setQuery(item.symbol);
                setDropdown(false);
              }}
            >
              <div className="flex flex-col">
                <span className="font-black text-yellow-500 text-sm">{item.symbol}</span>
                <span className="text-[10px] text-gray-400 truncate w-32">{item.name}</span>
              </div>
              {item.price && <span className="text-cyan-400 font-mono text-xs">${item.price.toFixed(2)}</span>}
            </li>
          ))}
        </ul>
      )}
      {loading && <div className="absolute right-2 top-2 text-[10px] text-yellow-500 animate-pulse">...</div>}
    </div>
  );
};




const AddEditPortfolioRealtimeModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  gurus?: GuruOption[];
  onCreateRealtimePortfolio: (
    guruId: string,
    portId: string,
    portfolioData: any
  ) => Promise<void>;
  editPortfolio?: RealtimePortfolio | null;
  dateKey: string; // <--- เพิ่มบรรทัดนี้
}> = ({ open, onClose, onCreated, gurus, onCreateRealtimePortfolio, editPortfolio,dateKey }) => {
  const [ownerName, setOwnerName] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [portfolioId, setPortfolioId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [availablePortfolios, setAvailablePortfolios] = useState<Portfolio[]>([]);
  const [buyRowsAP, setBuyRowsAP] = useState([{ symbol: '', amount: '', reason: '' }]);
  const [sellRowsAP, setSellRowsAP] = useState([{ symbol: '', amount: '', reason: '' }]);

  const [buyRowsAD, setBuyRowsAD] = useState([{ symbol: '', amount: '', reason: '' }]);
  const [sellRowsAD, setSellRowsAD] = useState([{ symbol: '', amount: '', reason: '' }]);
  const [buyRowsAS, setBuyRowsAS] = useState([
    {
      symbol: '',
      reason: '',
      numBatches: 1, // จำนวนไม้เริ่มต้น

      batches: [{ qty: 1, price: '' }], // เพิ่มข้อมูล batches
    },
  ]);
  const [sellRowsAS, setSellRowsAS] = useState([{ symbol: '', reason: '', numBatches: 1, batches: [{ qty: 1, price: '' }] }]);
  // ฟังก์ชันสำหรับเพิ่มแถว
  const addBuyRow = (type: 'AP' | 'AD' | 'AS') => {
    if (type === 'AP') setBuyRowsAP([...buyRowsAP, { symbol: '', amount: '', reason: '' }]);
    if (type === 'AD') setBuyRowsAD([...buyRowsAD, { symbol: '', amount: '', reason: '' }]);
    if (type === 'AS') setBuyRowsAS([...buyRowsAS, { symbol: '', reason: '', numBatches: 1, batches: [{ qty: 1, price: '' }] }]);
  };

  const addSellRow = (type: 'AP' | 'AD' | 'AS') => {
    if (type === 'AP') setSellRowsAP([...sellRowsAP, { symbol: '', amount: '', reason: '' }]);
    if (type === 'AD') setSellRowsAD([...sellRowsAD, { symbol: '', amount: '', reason: '' }]);
    if (type === 'AS') setSellRowsAS([...sellRowsAS, { symbol: '', reason: '', numBatches: 1, batches: [{ qty: 1, price: '' }] }]);
  };


  useEffect(() => {
    if (editPortfolio) {
      // ตั้งค่า ownerId และ ownerName
      setOwnerId(editPortfolio.guruId);
      const selectedGuru = gurus?.find((g) => g.id === editPortfolio.guruId);
      if (selectedGuru) {
        setOwnerName(selectedGuru.name);
        setAvailablePortfolios(selectedGuru.portfolios || []);
      }


      // ตั้งค่า portfolioId ให้ตรงกับชื่อพอร์ต
      const matchedPortfolio = selectedGuru?.portfolios.find(
        (portfolio) => portfolio.name === editPortfolio.portfolioName
      );
      if (matchedPortfolio) {
        setPortfolioId(matchedPortfolio.id);
      }

      // ตั้งค่า buys และ sells ตามประเภทของ guruType
      if (editPortfolio.guruType === 'AP') {
        setBuyRowsAP(
          editPortfolio.buys.map((buy) => ({
            symbol: buy.ticker,
            amount: '',
            reason: buy.reason,
          }))
        );
        setSellRowsAP(
          editPortfolio.sells.map((sell) => ({
            symbol: sell.ticker,
            amount: '',
            reason: sell.reason,
          }))
        );
      } else if (editPortfolio.guruType === 'AD') {
        setBuyRowsAD(
          editPortfolio.buys.map((buy) => ({
            symbol: buy.ticker,
            amount: buy.amount !== undefined ? buy.amount.toString() : '', // แปลง amount เป็น string
            reason: buy.reason,
          }))
        );
        setSellRowsAD(
          editPortfolio.sells.map((sell) => ({
            symbol: sell.ticker,
            amount: sell.amount !== undefined ? sell.amount.toString() : '', // แปลง amount เป็น string
            reason: sell.reason,
          }))
        );
      } else if (editPortfolio.guruType === 'AS') {
        setBuyRowsAS(
          editPortfolio.buys.map((buy) => ({
            symbol: buy.ticker,
            reason: buy.reason,
            numBatches: buy.batches?.length || 1,
            batches: buy.batches.map((batch) => ({
              qty: batch.qty,
              price: batch.price.toString(), // แปลง number เป็น string
            })),
          }))
        );
        setSellRowsAS(
          editPortfolio.sells.map((sell) => ({
            symbol: sell.ticker,
            reason: sell.reason,
            numBatches: sell.batches?.length || 1,
            batches: sell.batches.map((batch) => ({
              qty: batch.qty,
              price: batch.price.toString(), // แปลง number เป็น string
            })),
          }))
        );
      }
    } else {
      // รีเซ็ตค่าเมื่อไม่มี editPortfolio
      setOwnerId('');
      setOwnerName('');
      setPortfolioId('');
      setBuyRowsAP([{ symbol: '', amount: '', reason: '' }]);
      setSellRowsAP([{ symbol: '', amount: '', reason: '' }]);
      setBuyRowsAD([{ symbol: '', amount: '', reason: '' }]);
      setSellRowsAD([{ symbol: '', amount: '', reason: '' }]);
      setBuyRowsAS([{ symbol: '', reason: '', numBatches: 1, batches: [{ qty: 1, price: '' }] }]);
      setSellRowsAS([{ symbol: '', reason: '', numBatches: 1, batches: [{ qty: 1, price: '' }] }]);
    }
  }, [editPortfolio, gurus]);

  // เพิ่มแถวใหม่ใน batches
  const addBatchRow = (type: 'buy' | 'sell', index: number) => {
    if (type === 'buy') {
      const newRows = [...buyRowsAS];
      newRows[index].batches.push({ qty: 1, price: '' });
      setBuyRowsAS(newRows);
    } else if (type === 'sell') {
      const newRows = [...sellRowsAS];
      newRows[index].batches.push({ qty: 1, price: '' });
      setSellRowsAS(newRows);
    }
  };




  const handleRowChange = (
    type: 'buy' | 'sell',
    guruType: 'AP' | 'AD' | 'AS',
    index: number,
    field: string,
    value: string,
    batchIndex?: number,
    batchField?: 'qty' | 'price'
  ) => {
    if (type === 'buy') {

      if (guruType === 'AP') {
        const newRows = [...buyRowsAP];
        (newRows[index] as any)[field] = value;
        setBuyRowsAP(newRows);
      }
      if (guruType === 'AD') {
        const newRows = [...buyRowsAD];
        (newRows[index] as any)[field] = value;
        setBuyRowsAD(newRows);
      }
      if (guruType === 'AS') {
        const newRows = [...buyRowsAS];
        if (field === 'numBatches') {
          // แทนที่จะ parseInt ทันที ให้เช็คก่อนว่าถ้าเป็นค่าว่างก็ให้เป็น "" ไปก่อน
          const newValue = value === '' ? '' : parseInt(value, 10);
          const newRows = [...(type === 'buy' ? buyRowsAS : sellRowsAS)];

          // จัดการเรื่องการเพิ่ม/ลดจำนวนไม้ (ใช้ค่า 1 เป็นฐานถ้า value ว่าง)
          const effectiveNum = parseInt(value, 10) || 1;
          const currentBatches = newRows[index].batches;

          if (effectiveNum > currentBatches.length) {
            for (let i = currentBatches.length; i < effectiveNum; i++) {
              currentBatches.push({ qty: 1, price: '' });
            }
          } else if (effectiveNum < currentBatches.length) {
            currentBatches.splice(effectiveNum);
          }

          newRows[index].numBatches = newValue as any; // ยอมให้เป็นค่าว่างใน state
          type === 'buy' ? setBuyRowsAS(newRows) : setSellRowsAS(newRows);
          return; // จบการทำงานของเคส numBatches
        } else if (batchIndex !== undefined && batchField) {
          // แก้ตรงนี้: ยอมให้ qty และ price เป็นค่าว่างได้
          const parsedValue = (batchField === 'qty' || batchField === 'price')
            ? (value === '' ? '' : value) // เก็บเป็น string ว่างไว้ก่อน
            : value;
          (newRows[index].batches[batchIndex] as any)[batchField] = parsedValue;
        } else {
          (newRows[index] as any)[field] = value;
        }
        setBuyRowsAS(newRows);
      }
    } else {
      if (guruType === 'AP') {
        const newRows = [...sellRowsAP];
        (newRows[index] as any)[field] = value;
        setSellRowsAP(newRows);
      }
      if (guruType === 'AD') {
        const newRows = [...sellRowsAD];
        (newRows[index] as any)[field] = value;
        setSellRowsAD(newRows);
      }
      if (guruType === 'AS') {
        const newRows = [...sellRowsAS];
        if (field === 'numBatches') {
          // แก้ตรงนี้: ยอมให้ค่าว่างผ่านเข้าไปใน State เหมือนฝั่ง Buy
          const newValue = value === '' ? '' : parseInt(value, 10);

          // ใช้ 1 เป็นค่าพื้นฐานสำหรับจัดการจำนวน Array
          const effectiveNum = parseInt(value, 10) || 1;
          const currentBatches = newRows[index].batches;

          if (effectiveNum > currentBatches.length) {
            for (let i = currentBatches.length; i < effectiveNum; i++) {
              currentBatches.push({ qty: 1, price: '' });
            }
          } else if (effectiveNum < currentBatches.length) {
            currentBatches.splice(effectiveNum);
          }

          newRows[index].numBatches = newValue as any; // อัปเดต state ด้วยค่าที่อาจเป็นค่าว่าง
          setSellRowsAS(newRows);
          return;
        } else if (batchIndex !== undefined && batchField) {
          // แก้ตรงนี้: ยอมให้ qty และ price เป็นค่าว่างได้เหมือนฝั่ง Buy
          const parsedValue = (batchField === 'qty' || batchField === 'price')
            ? (value === '' ? '' : value)
            : value;
          (newRows[index].batches[batchIndex] as any)[batchField] = parsedValue;
        } else {
          (newRows[index] as any)[field] = value;
        }
        setSellRowsAS(newRows);
      }
    }
  };

  // useEffect(() => {
  //   if (gurus && gurus.length > 0) {
  //     setOwnerId(gurus[0].id);
  //     setOwnerName(gurus[0].name);
  //     setAvailablePortfolios(gurus[0]?.portfolios || []);
  //   }
  // }, [gurus]);

  const handleOwnerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOwnerId = e.target.value;
    setOwnerId(selectedOwnerId);
    const selectedGuru = gurus?.find((g) => g.id === selectedOwnerId);
    if (selectedGuru) {
      setOwnerName(selectedGuru.name);
      setAvailablePortfolios(selectedGuru?.portfolios || []);
      setPortfolioId('');
    }
  };
  // ฟังก์ชันสำหรับลบแถว
  const removeRow = (type: 'buy' | 'sell', guruType: 'AP' | 'AD' | 'AS', index: number) => {
    if (type === 'buy') {
      if (guruType === 'AP') setBuyRowsAP(buyRowsAP.filter((_, i) => i !== index));
      if (guruType === 'AD') setBuyRowsAD(buyRowsAD.filter((_, i) => i !== index));
      if (guruType === 'AS') setBuyRowsAS(buyRowsAS.filter((_, i) => i !== index));
    } else {
      if (guruType === 'AP') setSellRowsAP(sellRowsAP.filter((_, i) => i !== index));
      if (guruType === 'AD') setSellRowsAD(sellRowsAD.filter((_, i) => i !== index));
      if (guruType === 'AS') setSellRowsAS(sellRowsAS.filter((_, i) => i !== index));
    }
  };
  // ฟังก์ชันสำหรับลบแถวที่ว่างทั้งหมด
  const removeEmptyRows = () => {
    setBuyRowsAP(buyRowsAP.filter((row) => row.symbol.trim() || row.reason.trim()));
    setBuyRowsAD(buyRowsAD.filter((row) => row.symbol.trim() || row.reason.trim()));
    setBuyRowsAS(buyRowsAS.filter((row) => row.symbol.trim() || row.reason.trim()));
    setSellRowsAP(sellRowsAP.filter((row) => row.symbol.trim() || row.reason.trim()));
    setSellRowsAD(sellRowsAD.filter((row) => row.symbol.trim() || row.reason.trim()));
    setSellRowsAS(sellRowsAS.filter((row) => row.symbol.trim() || row.reason.trim()));
  };

  // เรียกใช้ removeEmptyRows เมื่อเปิด Modal
  useEffect(() => {
    if (open && !editPortfolio) {
      removeEmptyRows();
    }
  }, [open]);

  const handleCreate = async () => {
    if (!portfolioId) {
      setNameError('กรุณาเลือกพอร์ต');
      return;
    }
    setLoading(true);
    try {
      let portfolioData;

      // เปลี่ยนลำดับการเช็คเงื่อนไข
      if (ownerId === 'eba14023-edb7-4fed-aa59-0dd2ae07de46') {
        // AP - guru-ap
        portfolioData = {
          dateKey: dateKey, // <--- เพิ่มบรรทัดนี้
          holdings: [
            ...buyRowsAP.map((row) => ({
              ticker: row.symbol,
              action: 'BUY',
              reason: row.reason,
              narrative: '',
            })),
            ...sellRowsAP.map((row) => ({
              ticker: row.symbol,
              action: 'SELL',
              reason: row.reason,
              narrative: '',
            })),
          ],
        };
      } else if (ownerId === '2d51d1c5-e764-4503-9293-3a98c5d39123') {
        // AD - guru-ad (เฉพาะแอดอั้ม)
        portfolioData = {
          dateKey: dateKey, // <--- เพิ่มบรรทัดนี้
          holdings: [
            ...buyRowsAD.map((row) => ({
              ticker: row.symbol,
              action: 'BUY',
              amount: parseFloat(row.amount || '0'),
              reason: row.reason,
            })),
            ...sellRowsAD.map((row) => ({
              ticker: row.symbol,
              action: 'SELL',
              amount: parseFloat(row.amount || '0'),
              reason: row.reason,
            })),
          ],
        };
      } else {
        // AS - guru-as (กลายเป็น Default สำหรับกูรูทั่วไป)
        portfolioData = {
          dateKey: dateKey, // <--- เพิ่มบรรทัดนี้
          holdings: [
            ...buyRowsAS.map((row) => ({
              ticker: row.symbol,
              action: 'BUY',
              reason: row.reason,
              batches: row.batches.map((batch) => ({
                qty: batch.qty,
                price: parseFloat(batch.price || '0'),
              })),
            })),
            ...sellRowsAS.map((row) => ({
              ticker: row.symbol,
              action: 'SELL',
              reason: row.reason,
              batches: row.batches.map((batch) => ({
                qty: batch.qty,
                price: parseFloat(batch.price || '0'),
              })),
            })),
          ],
          
        };
      }


      if (editPortfolio) {
        // กรณีแก้ไข
        await onCreateRealtimePortfolio(ownerId, portfolioId, portfolioData);
        toast.success('แก้ไขพอร์ตสำเร็จ');
      } else {
        // กรณีสร้างใหม่
        await onCreateRealtimePortfolio(ownerId, portfolioId, portfolioData);
        toast.success('สร้างพอร์ตสำเร็จ');
      }
      // toast.success('สร้างพอร์ตสำเร็จ');
      onCreated();
      onClose();
    } catch (e: any) {
      toast.error(e.message || 'เกิดข้อผิดพลาด');
    } finally {
      setLoading(false);
    }
  };

  // คำนวณข้อมูลสำหรับ Donut Chart
  const chartData =
    ownerId === 'eba14023-edb7-4fed-aa59-0dd2ae07de46'
      ? buyRowsAP.map((row) => ({
        symbol: row.symbol,
        amount: parseFloat(row.amount || '0'),
      }))
      : ownerId === '2d51d1c5-e764-4503-9293-3a98c5d39123'
        ? buyRowsAD.map((row) => ({
          symbol: row.symbol,
          amount: parseFloat(row.amount || '0'),
        }))
        : buyRowsAS.map((row) => ({
          symbol: row.symbol,
          amount: row.batches.reduce(
            (sum, batch) => sum + batch.qty * parseFloat(batch.price || '0'),
            0
          ),
        }));

  const renderContentByOwner = () => {
    if (ownerId === 'eba14023-edb7-4fed-aa59-0dd2ae07de46') {
      return (
        <div className="flex flex-col md:flex-row h-full bg-[#1e1e24] p-2 md:p-4 gap-4">
          {/* ฝั่งการซื้อ (Buy) */}
          <div className="w-full md:w-1/2 md:pr-4 md:border-r border-gray-700">
            <h4 className="text-green-400 font-bold mb-4 flex items-center gap-2">การซื้อ (Buy)</h4>

            {/* Desktop Table (Hidden on Mobile) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-700">
                    <th className="py-2 text-left w-1/3">ชื่อหุ้น</th>
                    <th className="py-2 text-left">เหตุผล</th>
                  </tr>
                </thead>
                <tbody>
                  {buyRowsAP.map((row, index) => (
                    <tr key={index} className="border-b border-gray-800/50">
                      <td className="py-2 pr-2">
                        {/* <input
                          type="text"
                          placeholder="เช่น AXON"
                          value={row.symbol}
                          onChange={(e) => handleRowChange('buy', 'AP', index, 'symbol', e.target.value)}
                          className="w-full bg-[#2a2a32] text-white px-2 py-1 rounded border border-transparent focus:border-yellow-500 outline-none"
                        /> */}
                        <TickerDropdown
                          value={row.symbol}
                          onChange={(symbol) => {
                            // อัปเดต symbol และถ้ามีราคามาด้วย ให้เอาไปใส่ในไม้แรก (ถ้าต้องการ)
                            handleRowChange('buy', 'AP', index, 'symbol', symbol);

                          }}
                        />
                      </td>
                      <td className="py-2">
                        <input
                          type="text"
                          placeholder="เหตุผลการซื้อ"
                          value={row.reason}
                          onChange={(e) => handleRowChange('buy', 'AP', index, 'reason', e.target.value)}
                          className="w-full bg-[#2a2a32] text-white px-2 py-1 rounded border border-transparent focus:border-yellow-500 outline-none"
                        />
                      </td>
                      <td className="py-2 text-center">
                        <button
                          onClick={() => removeRow('buy', 'AP', index)}
                          className="text-red-500 hover:text-red-700 font-bold"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile Card List (Hidden on Desktop) */}
            <div className="md:hidden flex flex-col gap-3">
              {buyRowsAP.map((row, index) => (
                <div key={index} className="bg-[#2a2a32] p-3 rounded-lg relative border-l-4 border-green-500">
                  <button onClick={() => removeRow('buy', 'AP', index)} className="absolute top-2 right-2 text-red-500 text-xl">×</button>
                  <div className="mb-2">
                    <label className="text-[10px] text-gray-400">ชื่อหุ้น</label>
                    <TickerDropdown value={row.symbol} onChange={(s) => handleRowChange('buy', 'AP', index, 'symbol', s)} />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-400">เหตุผล</label>
                    <input
                      type="text"
                      value={row.reason}
                      onChange={(e) => handleRowChange('buy', 'AP', index, 'reason', e.target.value)}
                      className="w-full bg-[#18181b] text-white px-2 py-1.5 rounded border border-transparent focus:border-yellow-500 outline-none text-sm"
                      placeholder="เหตุผลการซื้อ"
                    />
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => addBuyRow('AP')}
              className="mt-4 w-full py-2 bg-[#333] hover:bg-[#444] text-gray-300 rounded-lg text-xs font-bold transition-all"
            >
              + เพิ่มรายการซื้อ
            </button>
          </div>

          {/* ฝั่งการขาย (Sell) */}
          <div className="w-full md:w-1/2 md:pl-4">
            <h4 className="text-red-400 font-bold mb-4 flex items-center gap-2">
              ขาย (Sell)
            </h4>

            {/* Desktop View: แสดงเป็น Table (ซ่อนบนมือถือ) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-700">
                    <th className="py-2 text-left w-1/3">ชื่อหุ้น</th>
                    <th className="py-2 text-left">เหตุผล</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {sellRowsAP.map((row, index) => (
                    <tr key={index} className="border-b border-gray-800/50">
                      <td className="py-2 pr-2">
                        <TickerDropdown
                          value={row.symbol}
                          onChange={(symbol) => handleRowChange('sell', 'AP', index, 'symbol', symbol)}
                        />
                      </td>
                      <td className="py-2">
                        <input
                          type="text"
                          placeholder="เหตุผลการขาย"
                          value={row.reason}
                          onChange={(e) => handleRowChange('sell', 'AP', index, 'reason', e.target.value)}
                          className="w-full bg-[#2a2a32] text-white px-2 py-1 rounded border border-transparent focus:border-red-500 outline-none"
                        />
                      </td>
                      <td className="py-2 text-center">
                        <button
                          onClick={() => removeRow('sell', 'AP', index)}
                          className="text-red-500 hover:text-red-700 font-bold"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile View: แสดงเป็น Card (ซ่อนบน Desktop) */}
            <div className="md:hidden flex flex-col gap-3">
              {sellRowsAP.map((row, index) => (
                <div key={index} className="bg-[#2a2a32] p-3 rounded-lg relative border-l-4 border-red-500">
                  <button
                    onClick={() => removeRow('sell', 'AP', index)}
                    className="absolute top-2 right-2 text-red-500 text-2xl font-light leading-none"
                  >
                    ×
                  </button>
                  <div className="mb-3">
                    <label className="text-[10px] text-gray-400 block mb-1 uppercase font-bold">ชื่อหุ้น</label>
                    <TickerDropdown
                      value={row.symbol}
                      onChange={(symbol) => handleRowChange('sell', 'AP', index, 'symbol', symbol)}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-400 block mb-1 uppercase font-bold">เหตุผล</label>
                    <input
                      type="text"
                      value={row.reason}
                      onChange={(e) => handleRowChange('sell', 'AP', index, 'reason', e.target.value)}
                      className="w-full bg-[#18181b] text-white px-2 py-2 rounded border border-transparent focus:border-red-500 outline-none text-sm"
                      placeholder="ทำกำไร / หลุดแนวรับ..."
                    />
                  </div>
                </div>
              ))}
              {/* {sellRowsAP.length === 0 && (
      <div className="text-center py-4 border-2 border-dashed border-gray-800 rounded-lg text-gray-500 text-xs">
        ยังไม่มีรายการขาย
      </div>
    )} */}
            </div>

            <button
              onClick={() => addSellRow('AP')}
              className="mt-4 w-full py-3 md:py-2 bg-[#333] hover:bg-[#444] text-gray-300 rounded-lg text-xs font-bold transition-all active:scale-[0.98]"
            >
              + เพิ่มรายการขาย
            </button>
          </div>
        </div>
      );
    } else if (ownerId === '2d51d1c5-e764-4503-9293-3a98c5d39123') {
      // รูปแบบที่ 3 (Default)
      return (
        <div className="flex flex-col md:flex-row h-full bg-[#1e1e24] p-4 gap-6">
          {/* ฝั่งซ้าย: วงกลมแสดงกราฟ (ซ่อนบน Mobile เพื่อประหยัดพื้นที่) */}
          <div className="hidden md:flex md:w-1/4 flex-col items-center justify-center border-r border-gray-700 pr-6">
            <DonutChart data={chartData} />
          </div>

          {/* ฝั่งขวา: รายการซื้อและขาย */}
          <div className="w-full md:w-3/4 flex flex-col gap-8 md:gap-6 md:max-h-[500px] pr-2 md:custom-scrollbar">

            {/* ส่วนการซื้อ (Buy) */}
            <section>
              <h4 className="text-lg font-bold mb-3 text-green-400 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                การซื้อ (Buy)
              </h4>

              {/* Desktop Table (Visible only on md+) */}
              <div className="hidden md:block">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="text-gray-400 border-b border-gray-700 bg-black/20">
                      <th className="py-2 px-2 text-left">ชื่อหุ้น</th>
                      <th className="py-2 px-2 text-left">จำนวนเงิน</th>
                      <th className="py-2 px-2 text-left">เหตุผล</th>
                      <th className="py-2 px-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {buyRowsAD.map((row, index) => (
                      <tr key={index}>
                        <td className="py-2 pr-2">
                          <TickerDropdown
                            value={row.symbol}
                            onChange={(symbol) => handleRowChange('buy', 'AD', index, 'symbol', symbol)}
                          />
                        </td>
                        <td className="py-2 pr-2">
                          <input
                            type="number"
                            value={row.amount}
                            onChange={(e) => handleRowChange('buy', 'AD', index, 'amount', e.target.value)}
                            className="w-full bg-[#2a2a32] text-white px-2 py-1.5 rounded border border-transparent focus:border-yellow-500 outline-none"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="py-2">
                          <input
                            type="text"
                            value={row.reason}
                            onChange={(e) => handleRowChange('buy', 'AD', index, 'reason', e.target.value)}
                            className="w-full bg-[#2a2a32] text-white px-2 py-1.5 rounded border border-transparent focus:border-yellow-500 outline-none"
                            placeholder="วิเคราะห์เทคนิค..."
                          />
                        </td>
                        <td className="text-center">
                          <button onClick={() => removeRow('buy', 'AD', index)} className="text-red-500 hover:text-red-400 font-bold px-2">×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card List (Visible only below md) */}
              <div className="md:hidden flex flex-col gap-3">
                {buyRowsAD.map((row, index) => (
                  <div key={index} className="bg-[#2a2a32] p-4 rounded-xl relative border-t-2 border-green-400 shadow-lg">
                    <button
                      onClick={() => removeRow('buy', 'AD', index)}
                      className="absolute top-2 right-2 text-red-500 text-2xl"
                    >
                      ×
                    </button>

                    {/* เปลี่ยนจาก Grid เป็น Flex Direction Column */}
                    <div className="flex flex-col gap-3">
                      {/* Ticker */}
                      <div>
                        <label className="text-[10px] text-gray-400 block mb-1 uppercase font-bold">Ticker</label>
                        <TickerDropdown value={row.symbol} onChange={(s) => handleRowChange('buy', 'AD', index, 'symbol', s)} />
                      </div>

                      {/* Amount ($) - แยกออกมาเป็นแถวตัวเอง */}
                      <div>
                        <label className="text-[10px] text-gray-400 block mb-1 uppercase font-bold">Amount ($)</label>
                        <input
                          type="number"
                          value={row.amount}
                          onChange={(e) => handleRowChange('buy', 'AD', index, 'amount', e.target.value)}
                          className="w-full bg-[#18181b] text-white px-3 py-2.5 rounded-lg border border-transparent focus:border-yellow-500 outline-none"
                          placeholder="0.00"
                        />
                      </div>

                      {/* Reason - แยกออกมาเป็นแถวตัวเอง */}
                      <div>
                        <label className="text-[10px] text-gray-400 block mb-1 uppercase font-bold">Reason</label>
                        <input
                          type="text"
                          value={row.reason}
                          onChange={(e) => handleRowChange('buy', 'AD', index, 'reason', e.target.value)}
                          className="w-full bg-[#18181b] text-white px-3 py-2.5 rounded-lg border border-transparent focus:border-yellow-500 outline-none"
                          placeholder="วิเคราะห์เทคนิค..."
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={() => addBuyRow('AD')} className="mt-3 text-[10px] font-black uppercase tracking-wider text-gray-400 hover:text-white transition-colors bg-white/5 px-4 py-3 md:py-1 rounded w-full md:w-auto">
                + เพิ่มรายการซื้อ
              </button>
            </section>

            {/* ส่วนการขาย (Sell) */}
            <section>
              <h4 className="text-lg font-bold mb-3 text-red-400 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                ขาย (Sell)
              </h4>

              {/* Desktop Table */}
              <div className="hidden md:block">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="text-gray-400 border-b border-gray-700 bg-black/20">
                      <th className="py-2 px-2 text-left">ชื่อหุ้น</th>
                      <th className="py-2 px-2 text-left">จำนวนเงิน</th>
                      <th className="py-2 px-2 text-left">เหตุผล</th>
                      <th className="py-2 px-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {sellRowsAD.map((row, index) => (
                      <tr key={index}>
                        <td className="py-2 pr-2">
                          <TickerDropdown value={row.symbol} onChange={(s) => handleRowChange('sell', 'AD', index, 'symbol', s)} />
                        </td>
                        <td className="py-2 pr-2">
                          <input
                            type="number"
                            value={row.amount}
                            onChange={(e) => handleRowChange('sell', 'AD', index, 'amount', e.target.value)}
                            className="w-full bg-[#2a2a32] text-white px-2 py-1.5 rounded border border-transparent focus:border-red-500 outline-none"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="py-2">
                          <input
                            type="text"
                            value={row.reason}
                            onChange={(e) => handleRowChange('sell', 'AD', index, 'reason', e.target.value)}
                            className="w-full bg-[#2a2a32] text-white px-2 py-1.5 rounded border border-transparent focus:border-red-500 outline-none"
                            placeholder="Take Profit..."
                          />
                        </td>
                        <td className="text-center">
                          <button onClick={() => removeRow('sell', 'AD', index)} className="text-red-500 hover:text-red-400 font-bold px-2">×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card List (Sell) */}
              <div className="md:hidden flex flex-col gap-3">
                {sellRowsAD.map((row, index) => (
                  <div key={index} className="bg-[#2a2a32] p-4 rounded-xl relative border-t-2 border-red-400 shadow-lg">
                    <button
                      onClick={() => removeRow('sell', 'AD', index)}
                      className="absolute top-2 right-2 text-red-500 text-2xl"
                    >
                      ×
                    </button>

                    {/* เปลี่ยนจาก Grid เป็น Flex Column เพื่อให้ Amount และ Reason อยู่คนละบรรทัด */}
                    <div className="flex flex-col gap-3">
                      {/* Ticker */}
                      <div>
                        <label className="text-[10px] text-gray-400 block mb-1 uppercase font-bold">Ticker</label>
                        <TickerDropdown value={row.symbol} onChange={(s) => handleRowChange('sell', 'AD', index, 'symbol', s)} />
                      </div>

                      {/* Amount ($) */}
                      <div>
                        <label className="text-[10px] text-gray-400 block mb-1 uppercase font-bold">Amount ($)</label>
                        <input
                          type="number"
                          value={row.amount}
                          onChange={(e) => handleRowChange('sell', 'AD', index, 'amount', e.target.value)}
                          className="w-full bg-[#18181b] text-white px-3 py-2.5 rounded-lg border border-transparent focus:border-red-500 outline-none"
                          placeholder="0.00"
                        />
                      </div>

                      {/* Reason */}
                      <div>
                        <label className="text-[10px] text-gray-400 block mb-1 uppercase font-bold">Reason</label>
                        <input
                          type="text"
                          value={row.reason}
                          onChange={(e) => handleRowChange('sell', 'AD', index, 'reason', e.target.value)}
                          className="w-full bg-[#18181b] text-white px-3 py-2.5 rounded-lg border border-transparent focus:border-red-500 outline-none"
                          placeholder="Take Profit / Cut loss..."
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={() => addSellRow('AD')} className="mt-3 text-[10px] font-black uppercase tracking-wider text-gray-400 hover:text-white transition-colors bg-white/5 px-4 py-3 md:py-1 rounded w-full md:w-auto">
                + เพิ่มรายการขาย
              </button>
            </section>
          </div>
        </div>
      );
    } else {
      return (
        <div className="flex flex-col md:flex-row h-full bg-[#1e1e24] p-2 md:p-4 gap-6">
          {/* ฝั่งซ้าย: วงกลมแสดงกราฟ (ซ่อนบน Mobile) */}
          <div className="hidden md:flex md:w-1/4 flex-col items-center justify-center border-r border-gray-700 pr-6">
            <DonutChart
              data={buyRowsAS.map((row) => ({
                symbol: row.symbol,
                amount: row.batches.reduce(
                  (sum, batch) => sum + (batch.qty * parseFloat(batch.price || '0')),
                  0
                ),
              }))}
            />
          </div>

          {/* ฝั่งขวา: รายการซื้อและขาย */}
          <div className="w-full md:w-3/4 flex flex-col gap-6 md:max-h-[500px] pr-2 ">

            {/* ส่วนการซื้อ (Buy) */}
            <section>
              <h4 className="text-lg font-bold mb-3 text-green-400 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                การซื้อ (Buy)
              </h4>

              {/* Desktop Table (ห้ามแก้เด็ดขาด) */}
              <div className="hidden md:block">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="text-gray-400 border-b border-gray-700 bg-black/20">
                      <th className="py-2 px-2 text-left">ชื่อหุ้น</th>
                      <th className="py-2 px-2 text-left">จำนวนไม้</th>
                      <th className="py-2 px-2 text-left">จำนวนหุ้น</th>
                      <th className="py-2 px-2 text-left">ราคาที่ซื้อ</th>
                      <th className="py-2 px-2 text-left">เหตุผล</th>
                      <th className="py-2 px-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {buyRowsAS.map((row, rowIndex) => (
                      <React.Fragment key={rowIndex}>
                        <tr className="border-t border-gray-700 text-white">
                          <td className="py-2 pr-2 align-top" rowSpan={row.batches.length}>
                            <TickerDropdown value={row.symbol} onChange={(s) => handleRowChange('buy', 'AS', rowIndex, 'symbol', s)} />
                          </td>
                          <td className="py-2 pr-2 align-top" rowSpan={row.batches.length}>
                            <input type="number" value={row.numBatches} onChange={(e) => handleRowChange('buy', 'AS', rowIndex, 'numBatches', e.target.value)} onBlur={(e) => (e.target.value === '' || parseInt(e.target.value) < 1) && handleRowChange('buy', 'AS', rowIndex, 'numBatches', '1')} className="w-full bg-[#2a2a32] text-white px-2 py-1.5 rounded border border-transparent outline-none" />
                          </td>
                          <td className="py-2 pr-2">
                            <input type="number" value={row.batches[0].qty} onChange={(e) => handleRowChange('buy', 'AS', rowIndex, '', e.target.value, 0, 'qty')} className="w-full bg-[#2a2a32] text-white px-2 py-1.5 rounded border border-transparent focus:border-yellow-500 outline-none" placeholder="0" />
                          </td>
                          <td className="py-2 pr-2">
                            <input type="number" value={row.batches[0].price} onChange={(e) => handleRowChange('buy', 'AS', rowIndex, '', e.target.value, 0, 'price')} className="w-full bg-[#2a2a32] text-white px-2 py-1.5 rounded border border-transparent focus:border-yellow-500 outline-none" placeholder="0.00" />
                          </td>
                          <td className="py-2 pr-2 align-top" rowSpan={row.batches.length}>
                            <input type="text" value={row.reason} onChange={(e) => handleRowChange('buy', 'AS', rowIndex, 'reason', e.target.value)} className="w-full bg-[#2a2a32] text-white px-2 py-1.5 rounded border border-transparent focus:border-yellow-500 outline-none" placeholder="เหตุผล" />
                          </td>
                          <td className="text-center" rowSpan={row.batches.length}>
                            <button onClick={() => removeRow('buy', 'AS', rowIndex)} className="text-red-500 hover:text-red-400 font-bold px-2">×</button>
                          </td>
                        </tr>
                        {row.batches.slice(1).map((batch, batchIndex) => (
                          <tr key={`${rowIndex}-${batchIndex}`}>
                            <td className="py-2 pr-2">
                              <input type="number" value={batch.qty} onChange={(e) => handleRowChange('buy', 'AS', rowIndex, '', e.target.value, batchIndex + 1, 'qty')} className="w-full bg-[#2a2a32] text-white px-2 py-1.5 rounded border border-transparent focus:border-yellow-500 outline-none" placeholder="0" />
                            </td>
                            <td className="py-2 pr-2">
                              <input type="number" value={batch.price} onChange={(e) => handleRowChange('buy', 'AS', rowIndex, '', e.target.value, batchIndex + 1, 'price')} className="w-full bg-[#2a2a32] text-white px-2 py-1.5 rounded border border-transparent focus:border-yellow-500 outline-none" placeholder="0.00" />
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card Layout (AS Buy) */}
              <div className="md:hidden flex flex-col gap-4">
                {buyRowsAS.map((row, rowIndex) => (
                  <div key={rowIndex} className="bg-[#2a2a32] p-4 rounded-xl relative border-t-4 border-green-500 shadow-xl">
                    <button onClick={() => removeRow('buy', 'AS', rowIndex)} className="absolute top-2 right-2 text-red-500 text-2xl font-bold">×</button>

                    <div className="flex gap-3 mb-3">
                      <div className="flex-1">
                        <label className="text-[10px] text-gray-400 block mb-1 uppercase font-bold tracking-wider">ชื่อหุ้น</label>
                        <TickerDropdown value={row.symbol} onChange={(s) => handleRowChange('buy', 'AS', rowIndex, 'symbol', s)} />
                      </div>
                      <div className="w-24">
                        <label className="text-[10px] text-gray-400 block mb-1 uppercase font-bold tracking-wider">Batches</label>
                        <input type="number" value={row.numBatches} onChange={(e) => handleRowChange('buy', 'AS', rowIndex, 'numBatches', e.target.value)} onBlur={(e) => (e.target.value === '' || parseInt(e.target.value) < 1) && handleRowChange('buy', 'AS', rowIndex, 'numBatches', '1')} className="w-full bg-[#18181b] text-white px-3 py-2 rounded-lg border border-transparent outline-none focus:border-yellow-500 font-bold" />
                      </div>
                    </div>

                    <div className="space-y-2 mb-3 bg-[#18181b]/50 p-2 rounded-lg">
                      <label className="text-[10px] text-yellow-500 block uppercase font-bold">รายละเอียดไม้</label>
                      {row.batches.map((batch, batchIndex) => (
                        <div key={batchIndex} className="grid grid-cols-2 gap-2 pb-2 border-b border-gray-700/50 last:border-0">
                          <div>
                            <span className="text-[9px] text-gray-500 block">ไม้ที่ {batchIndex + 1} (จำนวนหุ้น)</span>
                            <input type="number" value={batch.qty} onChange={(e) => handleRowChange('buy', 'AS', rowIndex, '', e.target.value, batchIndex, 'qty')} className="w-full bg-[#1e1e24] text-white px-2 py-1.5 rounded border border-transparent focus:border-green-500 outline-none text-xs" placeholder="หุ้น" />
                          </div>
                          <div>
                            <span className="text-[9px] text-gray-500 block">	ราคาที่ซื้อ ($)</span>
                            <input type="number" value={batch.price} onChange={(e) => handleRowChange('buy', 'AS', rowIndex, '', e.target.value, batchIndex, 'price')} className="w-full bg-[#1e1e24] text-white px-2 py-1.5 rounded border border-transparent focus:border-green-500 outline-none text-xs" placeholder="ราคาที่ซื้อ" />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div>
                      <label className="text-[10px] text-gray-400 block mb-1 uppercase font-bold">เหตุผล</label>
                      <input type="text" value={row.reason} onChange={(e) => handleRowChange('buy', 'AS', rowIndex, 'reason', e.target.value)} className="w-full bg-[#18181b] text-white px-3 py-2 rounded-lg border border-transparent focus:border-yellow-500 outline-none text-sm" placeholder="เหตุผล..." />
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={() => addBuyRow('AS')} className="mt-3 w-full py-3 bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all">+ เพิ่มรายการซื้อใหม่</button>
            </section>

            {/* ส่วนการขาย (Sell) - ทำเหมือนฝั่งซื้อแต่เปลี่ยนสีเป็นสีแดง */}
            <section>
              <h4 className="text-lg font-bold mb-3 text-red-400 flex items-center gap-2 pt-4 border-t border-gray-800">
                <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                ขาย (Sell)
              </h4>

              {/* Desktop Table (ห้ามแก้) */}
              <div className="hidden md:block">
                {/* ... โครงสร้างตารางเดิมของคุณ ... */}
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="text-gray-400 border-b border-gray-700 bg-black/20">
                      <th className="py-2 px-2 text-left">ชื่อหุ้น</th>
                      <th className="py-2 px-2 text-left">จำนวนไม้</th>
                      <th className="py-2 px-2 text-left">จำนวนหุ้น</th>
                      <th className="py-2 px-2 text-left">ราคาที่ขาย</th>
                      <th className="py-2 px-2 text-left">เหตุผล</th>
                      <th className="py-2 px-2 w-10"></th>
                    </tr></thead>

                  <tbody className="divide-y divide-gray-800">
      {sellRowsAS.map((row, rowIndex) => (
        <React.Fragment key={rowIndex}>
          {/* แถวที่ 1 ของหุ้นนั้นๆ */}
          <tr className="border-t border-gray-700 text-white">
            <td className="py-2 pr-2 align-top" rowSpan={row.batches.length}>
              <TickerDropdown value={row.symbol} onChange={(s) => handleRowChange('sell', 'AS', rowIndex, 'symbol', s)} />
            </td>
            <td className="py-2 pr-2 align-top" rowSpan={row.batches.length}>
              <input 
                type="number" 
                value={row.numBatches} 
                onChange={(e) => handleRowChange('sell', 'AS', rowIndex, 'numBatches', e.target.value)} 
                className="w-full bg-[#2a2a32] text-white px-2 py-1.5 rounded border border-transparent outline-none" 
              />
            </td>
            {/* รายละเอียดไม้แรก */}
            <td className="py-2 pr-2">
              <input 
                type="number" 
                value={row.batches[0].qty} 
                onChange={(e) => handleRowChange('sell', 'AS', rowIndex, '', e.target.value, 0, 'qty')} 
                className="w-full bg-[#2a2a32] text-white px-2 py-1.5 rounded border border-transparent focus:border-red-500 outline-none" 
                placeholder="0" 
              />
            </td>
            <td className="py-2 pr-2">
              <input 
                type="number" 
                value={row.batches[0].price} 
                onChange={(e) => handleRowChange('sell', 'AS', rowIndex, '', e.target.value, 0, 'price')} 
                className="w-full bg-[#2a2a32] text-white px-2 py-1.5 rounded border border-transparent focus:border-red-500 outline-none" 
                placeholder="0.00" 
              />
            </td>
            <td className="py-2 pr-2 align-top" rowSpan={row.batches.length}>
              <input 
                type="text" 
                value={row.reason} 
                onChange={(e) => handleRowChange('sell', 'AS', rowIndex, 'reason', e.target.value)} 
                className="w-full bg-[#2a2a32] text-white px-2 py-1.5 rounded border border-transparent focus:border-red-500 outline-none" 
                placeholder="เหตุผล" 
              />
            </td>
            <td className="text-center" rowSpan={row.batches.length}>
              <button onClick={() => removeRow('sell', 'AS', rowIndex)} className="text-red-500 hover:text-red-400 font-bold px-2">×</button>
            </td>
          </tr>

          {/* แถวที่ 2 เป็นต้นไป (ถ้ามีจำนวนไม้ > 1) */}
          {row.batches.slice(1).map((batch, batchIndex) => (
            <tr key={`${rowIndex}-${batchIndex}`} className="text-white">
              <td className="py-2 pr-2">
                <input 
                  type="number" 
                  value={batch.qty} 
                  onChange={(e) => handleRowChange('sell', 'AS', rowIndex, '', e.target.value, batchIndex + 1, 'qty')} 
                  className="w-full bg-[#2a2a32] text-white px-2 py-1.5 rounded border border-transparent focus:border-red-500 outline-none" 
                />
              </td>
              <td className="py-2 pr-2">
                <input 
                  type="number" 
                  value={batch.price} 
                  onChange={(e) => handleRowChange('sell', 'AS', rowIndex, '', e.target.value, batchIndex + 1, 'price')} 
                  className="w-full bg-[#2a2a32] text-white px-2 py-1.5 rounded border border-transparent focus:border-red-500 outline-none" 
                />
              </td>
            </tr>
          ))}
        </React.Fragment>
      ))}
    </tbody>
                </table>
              </div>

              {/* Mobile Card Layout (AS Sell) */}
              <div className="md:hidden flex flex-col gap-4">
                {sellRowsAS.map((row, rowIndex) => (
                  <div key={rowIndex} className="bg-[#2a2a32] p-4 rounded-xl relative border-t-4 border-red-500 shadow-xl">
                    <button onClick={() => removeRow('sell', 'AS', rowIndex)} className="absolute top-2 right-2 text-red-500 text-2xl font-bold">×</button>
                    <div className="flex gap-3 mb-3">
                      <div className="flex-1"><label className="text-[10px] text-gray-400 block mb-1 uppercase font-bold tracking-wider">ชื่อหุ้น</label><TickerDropdown value={row.symbol} onChange={(s) => handleRowChange('sell', 'AS', rowIndex, 'symbol', s)} /></div>
                      <div className="w-24"><label className="text-[10px] text-gray-400 block mb-1 uppercase font-bold tracking-wider">จำนวนไม้</label><input type="number" value={row.numBatches} onChange={(e) => handleRowChange('sell', 'AS', rowIndex, 'numBatches', e.target.value)} className="w-full bg-[#18181b] text-white px-3 py-2 rounded-lg border border-transparent outline-none focus:border-red-500 font-bold" /></div>
                    </div>
                    <div className="space-y-2 mb-3 bg-[#18181b]/50 p-2 rounded-lg">
                      {row.batches.map((batch, batchIndex) => (
                        <div key={batchIndex} className="grid grid-cols-2 gap-2 pb-2 border-b border-gray-700/50 last:border-0">
                          <div><span className="text-[9px] text-gray-500 block">ขายไม้ที่ {batchIndex + 1} (จำนวนหุ้น)</span><input type="number" value={batch.qty} onChange={(e) => handleRowChange('sell', 'AS', rowIndex, '', e.target.value, batchIndex, 'qty')} className="w-full bg-[#1e1e24] text-white px-2 py-1.5 rounded border border-transparent focus:border-red-500 outline-none text-xs" /></div>
                          <div><span className="text-[9px] text-gray-500 block">ราคาที่ขาย ($)</span><input type="number" value={batch.price} onChange={(e) => handleRowChange('sell', 'AS', rowIndex, '', e.target.value, batchIndex, 'price')} className="w-full bg-[#1e1e24] text-white px-2 py-1.5 rounded border border-transparent focus:border-red-500 outline-none text-xs" /></div>
                        </div>
                      ))}
                    </div>
                    <div><label className="text-[10px] text-gray-400 block mb-1 uppercase font-bold">เหตุผล</label><input type="text" value={row.reason} onChange={(e) => handleRowChange('sell', 'AS', rowIndex, 'reason', e.target.value)} className="w-full bg-[#18181b] text-white px-3 py-2 rounded-lg border border-transparent focus:border-red-500 outline-none text-sm" placeholder="TP / Cut loss..." /></div>
                  </div>
                ))}
              </div>

              <button onClick={() => addSellRow('AS')} className="mt-3 w-full py-3 bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all">+ เพิ่มรายการขายใหม่</button>
            </section>
          </div>
        </div>
      );
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 "
      style={{ scrollbarWidth: 'none' }}
      
    >
      <div
        className="relative bg-[#18181b] rounded-xl p-4 md:p-8 w-full max-w-[1200px] shadow-2xl transition-all flex flex-col h-[70vh] md:h-auto md:max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}
        style={{ background: '#18181b' }} // สีดำใกล้เคียงกับ Dashboard
      >
        {/* Header Title */}
        <h3 className="text-xl font-bold mb-6" style={{ color: 'var(--yellow-text-color)' }}>
          {editPortfolio ? 'แก้ไข' : 'สร้าง'} Realtime พอร์ต
        </h3>

        {/* Top Controls Grid */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Owner Selection */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium" style={{ color: 'var(--text-color)' }}>
              Owner Name
            </label>
            <select
              value={ownerId || ''} // ตั้งค่าเริ่มต้นเป็นค่าว่าง
              onChange={handleOwnerChange}
              className="w-full px-4 py-2.5 rounded-lg bg-[#23232b] text-white border border-[#333] focus:border-yellow-500 outline-none transition-all"
              disabled={!!editPortfolio} // ถ้ามี editPortfolio จะไม่สามารถแก้ไขได้
            >
              <option value="">เลือกกูรู</option> {/* เพิ่มตัวเลือกค่าเริ่มต้น */}
              {gurus?.map((guru) => (
                <option key={guru.id} value={guru.id}>
                  {guru.name}
                </option>
              ))}
            </select>
          </div>

          {/* Portfolio Selection */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium" style={{ color: 'var(--text-color)' }}>
              ชื่อพอร์ต
            </label>
            <select
              value={portfolioId}
              onChange={(e) => {
                setPortfolioId(e.target.value);
                setNameError(null);
              }}
              className="w-full px-4 py-2.5 rounded-lg bg-[#23232b] text-white border border-[#333] focus:border-yellow-500 outline-none transition-all"
              disabled={!ownerId || !!editPortfolio}
            >
              <option value="">เลือกพอร์ต</option>
              {availablePortfolios.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            {nameError && <div className="text-red-400 text-xs mt-1 font-medium">{nameError}</div>}
          </div>
        </div>

        {/* Dynamic Display Area (The White Box) */}
        {/* 2. Dynamic Content: ส่วนนี้จะเลื่อน (Scroll) ได้ส่วนเดียว */}
        <div className="flex-1 overflow-y-auto pr-1 custom-scrollbar mb-4">
          <div className="min-h-full">
            {renderContentByOwner()}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex-none flex justify-end gap-3 pt-4 border-t border-gray-800">
          <button
            className="flex-1 md:flex-none px-6 py-3 rounded-lg font-bold text-white bg-[#4b5563] active:scale-95 transition-all"
            onClick={onClose}
          >
            ยกเลิก
          </button>
          <button
            className="flex-1 md:flex-none px-8 py-3 rounded-lg font-bold transition-all active:scale-95 disabled:opacity-50"
            style={{
              backgroundColor: editPortfolio ? '#facc15' : '#22c55e',
              color: editPortfolio ? '#000' : '#fff'
            }}
            onClick={handleCreate}
            disabled={loading}
          >
            {loading ? '...' : editPortfolio ? 'แก้ไข' : 'สร้าง'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditPortfolioRealtimeModal;